<template>
    <ConfirmationModal
        additional-class-name="RoleChangeConfirmationModal"
        no="Cancel"
        yes="Change Role"
        :title="title"
        :progress="submitting"
        allow-close
        @input="onConfirm"
        @close="abort"
    >
        <RoleChangeConfirmationProcedureDescription
            :user-name="user.name"
            :role-label="newRoleLabel"
            :change-type="roleChangeType"
        />

        <RoleChangeConfirmationProcedureChangesContent
            :current-role-features="currentRoleFeatures"
            :new-role-features="newRoleFeatures"
            :role-change-type="roleChangeType"
        />
    </ConfirmationModal>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import {
        getAvailableFeaturesMapByRole,
    } from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/get-available-features-map-by-role/getAvailableFeaturesMapByRole';
    import { RbacFeature, RbacRole, RbacRoleDetails } from '@evidentid/tprm-portal-lib/models/rbac';
    import delay from '@evidentid/universal-framework/delay';
    import { User } from '@evidentid/user-management-api-client/types';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';

    import RoleChangeConfirmationProcedureChangesContent from './sub-components/RoleChangeConfirmationProcedureChangesContent.vue';
    import RoleChangeConfirmationProcedureDescription
        from './sub-components/RoleChangeConfirmationProcedureDescription.vue';
    import { RoleChangeType } from './type';
    import { useUserManagementStore } from '@/modules/user-management/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';

    interface UpdateStatus {
        status: OperationStatus;
        error: any;
    }

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    export default defineComponent({
        name: 'RoleChangeConfirmationProcedure',
        components: {
            ConfirmationModal,
            RoleChangeConfirmationProcedureDescription,
            RoleChangeConfirmationProcedureChangesContent,
        },
        props: {
            // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't
            // warn "Extraneous non-props attributes (uuid)" (see
            // UploadEntityDocumentProcedure.vue).
            uuid: {
                type: String,
                default: '',
            },
            rpName: {
                type: String,
                required: true,
            },
            user: {
                type: Object as PropType<User>,
                required: true,
            },
            newRole: {
                type: String as unknown as PropType<RbacRole>,
                required: true,
            },
        },
        emits: [ 'abort', 'finish' ],
        data() {
            return {
                RoleChangeType,
                // TODO[PRODUCT-24516]: use a proper fix instead of delay
                delayLoading: false,
            };
        },
        computed: {
            status(): UpdateStatus {
                return useUserManagementStore().updateUserStatus[this.rpName]?.[this.user.id] || {
                    status: OperationStatus.uninitialized,
                    error: null,
                };
            },
            submitting(): boolean {
                return this.status.status === OperationStatus.loading || this.delayLoading;
            },
            rolesAndPermissions(): RbacRoleDetails[] {
                return useRbacStore().userRoles.list || [];
            },
            newRoleFeaturesMap(): Record<RbacFeature, boolean> {
                return getAvailableFeaturesMapByRole(this.newRole, this.rolesAndPermissions);
            },
            currentRoleFeaturesMap(): Record<RbacFeature, boolean> {
                return getAvailableFeaturesMapByRole(this.user.role, this.rolesAndPermissions);
            },
            isContractExtractionEnabled(): boolean {
                return (this as any).$rp.currentDetails?.contractExtractionEnabled || false;
            },
            isGoAppEnabled(): boolean {
                return (this as any).$rp.currentDetails?.goAppEnabled || false;
            },
            hiddenFeatures(): RbacFeature[] {
                return [
                    ...(this.isContractExtractionEnabled ? [] : [ RbacFeature.dashboardUploadContract ]),
                    ...(this.isGoAppEnabled ? [] : [ RbacFeature.configurationGoAppSettings ]),
                ];
            },
            newRoleFeatures(): RbacFeature[] {
                return (Object.keys(this.newRoleFeaturesMap) as RbacFeature[])
                    .filter((key) => this.newRoleFeaturesMap[key] && !this.hiddenFeatures.includes(key));
            },
            currentRoleFeatures(): RbacFeature[] {
                return (Object.keys(this.currentRoleFeaturesMap) as RbacFeature[])
                    .filter((key) => this.currentRoleFeaturesMap[key] && !this.hiddenFeatures.includes(key));
            },
            newRoleLabel(): string {
                return this.rolesAndPermissions.find(({ name }) => name === this.newRole)?.label ||
                    String(this.newRole);
            },
            title(): string {
                const type = this.roleChangeType;
                return type === RoleChangeType.upgrade || type === RoleChangeType.downgrade
                    ? `Are you sure you want to ${type} this user?`
                    : 'Are you sure you want to change this user\'s role?';
            },
            roleChangeType(): RoleChangeType {
                const hasAllCurrentFeatures = this.currentRoleFeatures
                    .every((feature) => this.newRoleFeaturesMap[feature]);
                if (hasAllCurrentFeatures && this.newRoleFeatures.length > this.currentRoleFeatures.length) {
                    return RoleChangeType.upgrade;
                }
                const currentHasAllNewFeatures = this.newRoleFeatures
                    .every((feature) => this.currentRoleFeaturesMap[feature]);
                if (currentHasAllNewFeatures && this.newRoleFeatures.length < this.currentRoleFeatures.length) {
                    return RoleChangeType.downgrade;
                }
                return RoleChangeType.transfer;
            },
        },
        methods: {
            abort(): void {
                this.$emit('abort');
            },
            async onConfirm(isConfirmed: boolean): Promise<void> {
                if (isConfirmed) {
                    await this.updateUserRole();
                } else {
                    this.abort();
                }
            },
            async updateUserRole(): Promise<void> {
                if (this.status.status === OperationStatus.loading) {
                    return;
                }
                await useUserManagementStore().updateUser({
                    rpName: this.rpName,
                    id: this.user.id,
                    input: {
                        name: this.user.name,
                        email: this.user.email,
                        role: this.newRole,
                    },
                });
                // TODO[PRODUCT-24516]: use a proper fix instead of delay
                this.delayLoading = true;
                await delay(1000);
                this.delayLoading = false;

                this.performPostUpdateFinishOrError();
            },
            performPostUpdateFinishOrError(): void {
                const { status, error } = this.status;
                const response = error?.response;
                if (status === OperationStatus.success) {
                    this.success(`User Role for ${this.user.email} was updated`);
                    this.$emit('finish');
                } else if (error?.reason === 'forbidden' && response?.message?.includes?.('Not permitted to modify')) {
                    this.error(`
                        This action is not allowed since the user has multiple accounts with Evident.
                        Please reach out to Evident support to invite this user.
                    `);
                } else if (error?.reason === 'forbidden') {
                    this.error('You are not allowed to change selected user.');
                } else {
                    this.error('Oops, something went wrong. Please try again!');
                }
            },
            success(message: string): void {
                useSnackbarStore().displaySnackbar({ message, success: true });
            },
            error(message: string): void {
                useSnackbarStore().displaySnackbar({ message, success: false });
            },
        },
    });
</script>
